import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@/apollo';
import { 
  Trash2, 
  CheckCircle, 
  AlertTriangle, 
  X, 
  RefreshCw,
  Clock,
  Database,
  HardDrive
} from 'lucide-react';
import { CLEAR_CACHE } from '../../graphql/system-health';

interface ClearCacheModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type CacheClearStage = 'confirm' | 'clearing' | 'completed' | 'failed';

export const ClearCacheModal: React.FC<ClearCacheModalProps> = ({
  isOpen,
  onClose
}) => {
  const [stage, setStage] = useState<CacheClearStage>('confirm');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [clearResults, setClearResults] = useState<any>(null);

  const [clearCache] = useMutation(CLEAR_CACHE);

  const handleStartClearCache = async () => {
    try {
      setStage('clearing');
      setProgress(0);
      setError(null);
      setClearResults(null);

      // Show initial progress
      setCurrentStep('Starting cache clearing...');
      setProgress(10);

      // Call the actual cache clear mutation
      const result = await clearCache();
      
      if (result.data?.clearCache?.success) {
        setCurrentStep('Cache clearing completed successfully');
        setProgress(100);
        setClearResults(result.data.clearCache);
        setStage('completed');
      } else {
        throw new Error(result.data?.clearCache?.message || 'Cache clear failed');
      }
    } catch (err) {
      console.error('Cache clear error:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
      setStage('failed');
    }
  };

  const handleClose = () => {
    setStage('confirm');
    setProgress(0);
    setCurrentStep('');
    setError(null);
    setClearResults(null);
    onClose();
  };

  const getStageContent = () => {
    switch (stage) {
      case 'confirm':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
              <Trash2 className="w-8 h-8" style={{ color: 'var(--color-warning)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Clear Cache
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              This will clear all application and database caches. This may temporarily slow down the application as caches are rebuilt.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-info-bg)', borderColor: 'var(--color-info)' }}>
              <div className="flex items-start">
                <HardDrive className="w-5 h-5 mr-2 mt-0.5" style={{ color: 'var(--color-info)' }} />
                <div>
                  <h4 className="font-medium mb-1" style={{ color: 'var(--color-info)' }}>What will be cleared:</h4>
                  <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                    <li>• Application cache</li>
                    <li>• Database query cache</li>
                    <li>• Session cache</li>
                    <li>• Temporary files</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStartClearCache}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                style={{ backgroundColor: 'var(--color-warning)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-warning-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-warning)';
                }}
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear Cache</span>
              </button>
            </div>
          </div>
        );

      case 'clearing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-spin" style={{ backgroundColor: 'var(--color-accent-bg)' }}>
              <RefreshCw className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Clearing Cache
            </h3>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              {currentStep}
            </p>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
              <div 
                className="h-2 rounded-full transition-all duration-500"
                style={{ 
                  backgroundColor: 'var(--color-accent)',
                  width: `${progress}%`
                }}
              />
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              <Clock className="w-4 h-4" />
              <span>Please wait while we clear the cache...</span>
            </div>
          </div>
        );

      case 'completed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-success-bg)' }}>
              <CheckCircle className="w-8 h-8" style={{ color: 'var(--color-success)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Cache Cleared Successfully
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              All caches have been cleared. The application will rebuild caches as needed, which may cause slightly slower initial responses.
            </p>
            
            {clearResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-success-bg)', borderColor: 'var(--color-success)' }}>
                <div className="flex items-center mb-2">
                  <Database className="w-5 h-5 mr-2" style={{ color: 'var(--color-success)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-success)' }}>Cache Clear Results</h4>
                </div>
                <div className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <div>• Status: {clearResults.success ? 'Success' : 'Failed'}</div>
                  <div>• Message: {clearResults.message}</div>
                  {clearResults.details && (
                    <div>• Details: {clearResults.details}</div>
                  )}
                </div>
              </div>
            )}
            
            <button
              onClick={handleClose}
              className="px-4 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-success)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success)';
              }}
            >
              Close
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-error-bg)' }}>
              <AlertTriangle className="w-8 h-8" style={{ color: 'var(--color-error)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Cache Clear Failed
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {error || 'An unexpected error occurred while clearing the cache.'}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Close
              </button>
              <button
                onClick={handleStartClearCache}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                style={{ backgroundColor: 'var(--color-accent)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                }}
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="rounded-xl shadow-xl max-w-md w-full mx-4"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Clear Cache
          </h3>
          {stage === 'completed' || stage === 'failed' ? (
            <button
              onClick={handleClose}
              className="p-1 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          ) : null}
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {getStageContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
